import { uniq } from 'lodash'
import { makeAutoObservable, observable, ObservableSet, values } from 'mobx'
import { CommentStatus, LayerStatus, WorkflowReferenceType, ArticleView } from 'types'
import type { DocStore } from './DocStore'
import { CollabItem, CollabItemType } from './DocStore.types'
import type { InformalFeedbackRequest } from 'api/clients/InformalFeedbackRequestsClient'

export type CollabItemsFilter =
  | CollabItemsTypeFilter
  | CollabItemsUserFilter
  | CollabItemsReviewFilter
  | CollabItemsResolvedFilter

export type CollabItemsFacet = 'itemType' | 'review' | 'user' | 'resolved' | 'all'

type FacetFilter<T extends CollabItemsFacet> = {
  key: string
  label?: string
  fn: (item: CollabItem) => boolean
  facet: T
}

type reviewRequestFilter = {
  key: string
  label: string
  fn: (item: CollabItem, currentReviewRequest: InformalFeedbackRequest) => boolean
}

export type CollabItemsTypeFilter = {
  key: string
  fn: (item: CollabItem) => boolean
  type: CollabItemType
  label: string
  isDisabled: (docStore: DocStore) => boolean
} & FacetFilter<'itemType'>

export type CollabItemsUserFilter = {
  userId: number
} & FacetFilter<'user'>

export type CollabItemsReviewFilter = FacetFilter<'review'>

export type CollabItemsResolvedFilter = {
  key: string
  fn: (item: CollabItem) => boolean
  label: string
} & FacetFilter<'resolved'>

export const baseFilters: CollabItemsTypeFilter[] = [
  {
    key: 'comments',
    fn: (item) => item.type === CollabItemType.Comment,
    type: CollabItemType.Comment,
    facet: 'itemType',
    label: 'Comments',
    isDisabled: (docStore) => docStore.comments.length === 0,
  },
  {
    key: 'todos',
    fn: (item) => item.type === CollabItemType.Todo,
    type: CollabItemType.Todo,
    facet: 'itemType',
    label: 'Tasks',
    isDisabled: (docStore) => docStore.todos.length === 0,
  },
  {
    key: 'trackedChanges',
    fn: (item) => item.type === CollabItemType.TrackedChange,
    type: CollabItemType.TrackedChange,
    facet: 'itemType',
    label: 'Tracked Changes',
    isDisabled: (docStore) => docStore.trackedChanges.length === 0,
  },
]

export const reviewsFilter: CollabItemsReviewFilter = {
  key: 'reviews',
  fn: ({ item, type }) => {
    switch (type) {
      case CollabItemType.Comment:
      case CollabItemType.Todo:
        return item.workflow_references.some((wr) => wr.workflow_type === WorkflowReferenceType.InformalFeedbackRequest)
      case CollabItemType.TrackedChange:
        // Tracked changes are always considered part of the current review
        return true
    }
  },
  facet: 'review',
}

// Not one of the sidebar filters, used to filter comments by one specific review request
export const reviewFilter: reviewRequestFilter = {
  key: 'review',
  label: 'reviews',
  fn: ({ item, type }: CollabItem, reviewRequest: InformalFeedbackRequest) => {
    switch (type) {
      case CollabItemType.Comment:
      case CollabItemType.Todo:
        return item.workflow_references.some(
          (wr) =>
            wr.workflow_type === WorkflowReferenceType.InformalFeedbackRequest && wr.workflow_key === reviewRequest.key,
        )
      case CollabItemType.TrackedChange:
        // Tracked changes are always considered part of the current review
        return true
    }
  },
}

export const resolvedFilter: CollabItemsResolvedFilter = {
  key: 'resolved',
  fn: ({ item, type }) => {
    switch (type) {
      case CollabItemType.Comment:
        return item.status === CommentStatus.Resolved
      case CollabItemType.Todo:
        return Boolean(item.completed_at)
      case CollabItemType.TrackedChange:
        return false
    }
  },
  facet: 'resolved',
  label: 'Resolved',
}

export class CollabItemsFilters {
  docStore: DocStore
  activeKeys: ObservableSet<string> = observable.set()

  constructor(docStore: DocStore) {
    makeAutoObservable(this)
    this.docStore = docStore
  }

  activate = (key: string) => {
    this.activeKeys.add(key)
  }

  deactivate = (key: string) => {
    this.activeKeys.delete(key)
  }

  clear() {
    this.activeKeys.clear()
  }

  get inactive() {
    return this.all.filter((f) => !this.activeKeys.has(f.key))
  }

  get active() {
    return values(this.activeKeys)
      .map((key) => this.all.find((f) => f.key === key))
      .filter(Boolean) as CollabItemsFilter[]
  }

  get activeBaseKeys() {
    return values(this.activeKeys).filter((item) => baseFilters.map((baseFilter) => baseFilter.key).includes(item))
  }

  get activeNonUser() {
    return this.active.filter((item) =>
      [...baseFilters, resolvedFilter].map((baseFilter) => baseFilter.key).includes(item.key),
    )
  }

  get usersFilters(): CollabItemsUserFilter[] {
    const commentsUserIds = this.docStore.comments.map((comment) => comment.user.id)
    const todosUserIds = this.docStore.todos.map((todo) => todo.creator.id)
    const trackedChangesUserIds = this.docStore.trackedChanges.map((trackedChange) => trackedChange.userId)
    const sources = [...commentsUserIds, ...todosUserIds, ...trackedChangesUserIds]
    const userIds = uniq(sources.filter((id) => id !== undefined)) as number[]

    return userIds.map((userId) => ({
      key: `user-${userId}`,
      fn: ({ item, type }) => {
        switch (type) {
          case CollabItemType.Comment:
            return item.user.id === userId
          case CollabItemType.Todo:
            return item.creator.id === userId
          case CollabItemType.TrackedChange:
            return item.userId === userId
        }
      },
      userId,
      facet: 'user',
    }))
  }

  get all(): CollabItemsFilter[] {
    if (this.docStore.collabItems.length === 0) {
      return []
    }

    if (this.docStore.articleView === ArticleView.Commenting) {
      return [...this.usersFilters, resolvedFilter]
    }

    if (this.docStore.layerStatus === LayerStatus.DRAFT) {
      return [...baseFilters, ...this.usersFilters, resolvedFilter]
    }

    return [...baseFilters, ...this.usersFilters, reviewsFilter, resolvedFilter]
  }

  count = (filter: CollabItemsFilter) => {
    if (filter === resolvedFilter) {
      return this.docStore.collabItems.filter(resolvedFilter.fn).length
    }
    return this.docStore.filteredCollabItems.filter(filter.fn).length
  }
}
